/**
 * Krok UXR2 — HOTOVY MAPOVY VYREZ PRIPADU (ortofoto) pro hlavni nahled
 * detailu zakazky. Resi NALEZ UXR1-6/5: predloha ma vpravo ortofoto, kit
 * `MapPreview` kresli zakladni mapu a prepinat podklad v kitu bylo mimo
 * zadani UXR1.
 *
 * Oba endpointy jsou CTENI (`GET`) — vyrez se tu NEOBJEDNAVA. Vykresleni
 * (`POST …/offer/graphic`) tece pres WMS CUZK, stoji egress a patri k
 * nabidce, ne k otevreni obrazovky. Kdyz hotovy vyrez neexistuje, nahled
 * spadne na fotku / mapu adresy (`CaseThumb`) — nic se nepredstira.
 */

import { apiClient } from './api.js';
import { unwrap } from './api-error.js';
import { mediaSrc } from './properties.js';

/**
 * Jen to, co nahled potrebuje. Cely DTO vyrezu (`ApiPaths[…]`) ma `bbox` jako
 * ctyrprvkovou tuple, kterou `openapi-typescript` rozsiruje na `number[]` —
 * dva nekompatibilni tvary tehoz typu. Nahled bbox necte, takze se tu
 * nedeklaruje (zuzeni, ne pretypovani).
 */
export interface CaseMapSummary {
  readonly id: string;
  readonly layer: string;
  readonly status: string;
  readonly licenseNote: string | null;
  readonly createdAt: string;
  /** Krok NAP16 (A31): uloha vedle obrazku nese i 3D model (GLB). */
  readonly hasModel: boolean;
}

/** Poradi podkladu od nejlepsiho pro nahled: ortofoto s katastrem, pak ciste ortofoto. */
const HERO_LAYER_ORDER: readonly string[] = ['cadastral_orthophoto', 'orthophoto'];

export async function listCaseMaps(ddCaseId: string): Promise<readonly CaseMapSummary[]> {
  const result = await unwrap(
    apiClient.GET('/v1/duediligence/cases/{id}/maps', { params: { path: { id: ddCaseId } } }),
  );
  return result.items.map((item) => ({
    id: item.id,
    layer: item.layer,
    status: item.status,
    licenseNote: item.licenseNote,
    createdAt: item.createdAt,
    hasModel: item.hasModel,
  }));
}

/**
 * Krok UXP4a bod 1 (nalez P2-1): adresa pro `<img src>` MIRI NA API, ne na S3.
 * Podepsana URL (`…/maps/:mapId/url`) se sklada proti `S3_PUBLIC_ENDPOINT`,
 * ktery je na preview vnitrni (`http://127.0.0.1:9000`) — prohlizec ji zahodil
 * na Mixed Content + CSP a nahled byl na kazde proverene zakazce rozbity.
 * `mediaSrc` je TENTYZ prevod relativni cesty na absolutni, jaky pouzivaji
 * fotky nemovitosti (`lib/properties.ts`, ROZHODNUTI O6).
 */
export function caseMapImageUrl(ddCaseId: string, mapId: string): string {
  return mediaSrc(`/v1/duediligence/cases/${ddCaseId}/maps/${mapId}/image`);
}

/**
 * Krok NAP16 (A31) — 3D MODEL DOMU K NAHLEDU V MAPE.
 *
 * Model GLB i jeho nahledovy obrazek vyrabi JEDNA uloha fronty (vrstva
 * `house_3d`, Blender), takze oba odkazy patri k temuz `mapId`. Adresy miri
 * NA API, ne na S3 — tentyz duvod jako u obrazku vyse (UXP4a bod 1).
 *
 * `null` = beh model nevyrobil (prepinac `HOUSE_3D_ENABLED` je vychozi
 * vypnuty). Panel pak o 3D modelu MLCI; slib „model doplnime" by byl tvrzeni
 * o necem, co nikdo nezaradil.
 */
export interface CaseHouseModel {
  readonly mapId: string;
  readonly imageUrl: string;
  readonly modelUrl: string;
  readonly licenseNote: string | null;
}

/** Adresa modelu GLB jedne ulohy (`…/maps/:mapId/model`). */
export function caseMapModelUrl(ddCaseId: string, mapId: string): string {
  return mediaSrc(`/v1/duediligence/cases/${ddCaseId}/maps/${mapId}/model`);
}

export async function caseHouse3dModel(ddCaseId: string): Promise<CaseHouseModel | null> {
  const newest = (await listCaseMaps(ddCaseId))
    .filter((map) => map.status === 'DONE' && map.layer === 'house_3d' && map.hasModel)
    .reduce<CaseMapSummary | null>(
      (best, map) => (best === null || map.createdAt > best.createdAt ? map : best),
      null,
    );
  if (newest === null) return null;
  return {
    mapId: newest.id,
    imageUrl: caseMapImageUrl(ddCaseId, newest.id),
    modelUrl: caseMapModelUrl(ddCaseId, newest.id),
    licenseNote: newest.licenseNote,
  };
}

export interface CaseHeroImage {
  readonly url: string;
  /** Licencni poznamka vyrezu — NESMI se skryvat (stejne jako atribuce dlazdic). */
  readonly licenseNote: string | null;
}

/**
 * Nejlepsi HOTOVY vyrez pripadu (ortofoto s katastrem, pak ciste ortofoto),
 * z vice vyrezu tehoz podkladu NEJNOVEJSI — vyrez se po zmene rozsahu
 * zakazky kresli znovu.
 *
 * Krok MAP3b: vybira ho i EXPORT (`E4` PNG, `J1` PDF prehledu), proto vraci
 * `id` vyrezu, ne hotovou adresu obrazku — dva vybery „ktery vyrez je ten
 * spravny" by se casem rozesly (CLAUDE.md §8 bod 2).
 */
export async function caseExportMap(ddCaseId: string): Promise<CaseMapSummary | null> {
  const maps = (await listCaseMaps(ddCaseId)).filter((map) => map.status === 'DONE');
  for (const layer of HERO_LAYER_ORDER) {
    const newest = maps
      .filter((map) => map.layer === layer)
      .reduce<CaseMapSummary | null>(
        (best, map) => (best === null || map.createdAt > best.createdAt ? map : best),
        null,
      );
    if (newest !== null) return newest;
  }
  return null;
}

/** Ortofoto pripadu jako nahled, nebo `null`. */
export async function caseHeroImage(ddCaseId: string): Promise<CaseHeroImage | null> {
  const newest = await caseExportMap(ddCaseId);
  return newest === null
    ? null
    : { url: caseMapImageUrl(ddCaseId, newest.id), licenseNote: newest.licenseNote };
}
